import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { WINGS } from './wings';
import { useLean } from '../../context/LeanContext';
import ThemeToggle from './ThemeToggle';
import './navbar.css';

/**
 * Two tiers.
 *
 * Tier 1 is the same on every page: mark, the work|life switch, contact.
 * The switch is the site's primary wayfinding device, so it stays visible
 * at every breakpoint — it is never collapsed behind a hamburger. That is
 * what let the old drawer (and its missing aria-expanded, missing Escape
 * handler, and missing focus trap) be deleted outright.
 *
 * Tier 2 appears only inside a wing and carries that wing's sections as a
 * scrollable chip row on narrow screens.
 */
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { setLean } = useLean();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const wing = pathname.startsWith('/work')
    ? 'work'
    : pathname.startsWith('/life')
      ? 'life'
      : null;

  const sections = wing ? WINGS[wing].sections : [];

  return (
    <header className={`nav ${scrolled ? 'nav--scrolled' : ''}`}>
      <nav className="nav__bar" aria-label="Primary">
        <div className="container nav__inner">
          <Link to="/" className="nav__mark">suraj.</Link>

          {/* Real links, not buttons: works without JS, supports
              open-in-new-tab, and reads as navigation to a screen reader.
              Pointing at one is pointing at a wing, so it tips the home
              page balance like the pans and door cards do. */}
          <div className="nav__switch" role="group" aria-label="Site areas">
            {['work', 'life'].map((key) => (
              <NavLink
                key={key}
                to={WINGS[key].path}
                className={({ isActive }) =>
                  `nav__switch-item nav__switch-item--${key} ${isActive ? 'is-active' : ''}`
                }
                aria-current={wing === key ? 'true' : undefined}
                onMouseEnter={() => setLean(key)}
                onMouseLeave={() => setLean('none')}
                onFocus={() => setLean(key)}
                onBlur={() => setLean('none')}
              >
                {WINGS[key].label}
              </NavLink>
            ))}
          </div>

          <div className="nav__end">
            <ThemeToggle />
            <a className="nav__contact" href="/#connect">say hi</a>
          </div>
        </div>
      </nav>

      {sections.length > 0 && (
        <nav className="nav__sub" aria-label={`${WINGS[wing].label} sections`}>
          <div className="container nav__sub-inner">
            {sections.map((s) => (
              s.external ? (
                <a
                  key={s.label}
                  className="nav__sub-link"
                  href={s.to}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {s.label}
                </a>
              ) : (
                <NavLink
                  key={s.label}
                  to={s.to}
                  end={s.end}
                  className={({ isActive }) =>
                    `nav__sub-link ${isActive ? 'is-active' : ''}`
                  }
                >
                  {s.label}
                </NavLink>
              )
            ))}
          </div>
        </nav>
      )}
    </header>
  );
};

export default Navbar;
